import { z } from '@repo/schema'
import { isSupportedCountry, parsePhoneNumberFromString } from 'libphonenumber-js/max'

const isValidInternationalPhone = (value: string): boolean => {
  if (!value) return false
  try {
    const parsed = parsePhoneNumberFromString(value)
    return parsed?.isValid() ?? false
  } catch {
    return false
  }
}

type CountryMismatchOptions = {
  countryMismatchMessage?: (parsedCountry: string, selectedCountry: string) => string
}

const getPhoneCountryMismatch = (
  value: string,
  country: string,
  options?: CountryMismatchOptions,
): string | undefined => {
  if (!value || !country) return undefined
  try {
    const upperCountry = country.toUpperCase()
    const parsed = parsePhoneNumberFromString(
      value,
      isSupportedCountry(upperCountry) ? upperCountry : undefined,
    )
    if (!parsed) return 'invalid'
    if (parsed.country && parsed.country !== country.toUpperCase()) {
      const defaultMessage = `Phone number country is ${parsed.country}, not ${country.toUpperCase()}`
      return (
        options?.countryMismatchMessage?.(parsed.country, country.toUpperCase()) ?? defaultMessage
      )
    }
    if (!parsed.isValid()) return 'invalid'
    return undefined
  } catch {
    return 'invalid'
  }
}

export const PhoneCountryCodeSchema = z.string().length(2)

export const OptionalPhoneCountryCodeSchema = z.union([z.literal(''), PhoneCountryCodeSchema])

export const createPhoneNumberFieldSchema = (options: { requiredMessage?: string } = {}) => {
  const { requiredMessage } = options
  const base = z.string().trim()
  if (!requiredMessage) {
    return base
  }
  return base.min(1, requiredMessage)
}

export const createNullablePhoneNumberFieldSchema = () =>
  z
    .string()
    .trim()
    .transform(value => value || null)

export const PhoneNumberSchema = z
  .string()
  .trim()
  .refine(isValidInternationalPhone, 'Invalid phone number')

export type CreatePhoneNumberSchemaOptions = {
  invalidMessage?: string
  requiredMessage?: string
}

export const createPhoneNumberSchema = (options: CreatePhoneNumberSchemaOptions = {}) => {
  const { invalidMessage = 'Invalid phone number', requiredMessage } = options
  const base = z.string().trim()
  if (!requiredMessage) {
    return base.refine(isValidInternationalPhone, invalidMessage)
  }
  return base
    .min(1, requiredMessage)
    .refine(v => v === '' || isValidInternationalPhone(v), invalidMessage)
}

export const PhoneWithCountrySchema = z
  .object({
    phone: z.string().trim(),
    phone_country: PhoneCountryCodeSchema,
  })
  .superRefine((data, ctx) => {
    if (!data.phone) return
    const mismatch = getPhoneCountryMismatch(data.phone, data.phone_country)
    if (mismatch === 'invalid') {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid phone number',
        path: ['phone'],
      })
    } else if (mismatch) {
      ctx.addIssue({
        code: 'custom',
        message: mismatch,
        path: ['phone'],
      })
    }
  })

export type CreatePhoneWithCountrySchemaOptions = {
  countryMismatchMessage?: (parsedCountry: string, selectedCountry: string) => string
  countryRequiredMessage?: string
  invalidMessage?: string
  nullish?: boolean
  requiredMessage?: string
}

export const createPhoneWithCountrySchema = (options: CreatePhoneWithCountrySchemaOptions = {}) => {
  const {
    countryMismatchMessage,
    countryRequiredMessage,
    invalidMessage = 'Invalid phone number',
    nullish = false,
    requiredMessage,
  } = options

  let phoneSchema
  if (nullish) {
    phoneSchema = z
      .string()
      .trim()
      .transform(v => v || null)
  } else if (requiredMessage) {
    phoneSchema = z.string().trim().min(1, requiredMessage)
  } else {
    phoneSchema = z.string().trim()
  }

  let countrySchema
  if (nullish) {
    countrySchema = z.union([z.literal(''), PhoneCountryCodeSchema])
  } else if (countryRequiredMessage) {
    countrySchema = z
      .string()
      .trim()
      .min(1, countryRequiredMessage)
      .length(2, 'Please select a country')
  } else {
    countrySchema = PhoneCountryCodeSchema
  }

  return z
    .object({
      phone: phoneSchema,
      phone_country: countrySchema,
    })
    .superRefine((data, ctx) => {
      const phone = data.phone
      if (!phone) return
      const country = data.phone_country
      const countryStr = typeof country === 'string' ? country : ''
      if (countryStr) {
        const mismatch = getPhoneCountryMismatch(phone, countryStr, { countryMismatchMessage })
        if (mismatch === 'invalid') {
          ctx.addIssue({
            code: 'custom',
            message: invalidMessage,
            path: ['phone'],
          })
        } else if (mismatch) {
          ctx.addIssue({
            code: 'custom',
            message: mismatch,
            path: ['phone'],
          })
        }
      } else {
        if (!isValidInternationalPhone(phone)) {
          ctx.addIssue({
            code: 'custom',
            message: invalidMessage,
            path: ['phone'],
          })
        }
      }
    })
}

export type AddPhoneWithCountryValidationOptions = {
  countryMismatchMessage?: (parsedCountry: string, selectedCountry: string) => string
  invalidMessage?: string
  path?: string
}

export const addPhoneWithCountryValidation = (
  ctx: z.RefinementCtx,
  phone: null | string | undefined,
  country: null | string | undefined,
  options: AddPhoneWithCountryValidationOptions = {},
) => {
  const {
    countryMismatchMessage,
    invalidMessage = 'Invalid phone number',
    path = 'phone',
  } = options
  if (!phone) return
  if (!country) {
    if (!isValidInternationalPhone(phone)) {
      ctx.addIssue({
        code: 'custom',
        message: invalidMessage,
        path: [path],
      })
    }
    return
  }
  const mismatch = getPhoneCountryMismatch(phone, country, { countryMismatchMessage })
  if (mismatch === 'invalid') {
    ctx.addIssue({
      code: 'custom',
      message: invalidMessage,
      path: [path],
    })
  } else if (mismatch) {
    ctx.addIssue({
      code: 'custom',
      message: mismatch,
      path: [path],
    })
  }
}
